import React, { useState } from 'react';
import { InvisibleSmartCaptcha } from '@yandex/smart-captcha';
import { envConfig } from 'common/config';
import { setCaptchaToken } from 'common/helpers/axios.helper';

interface IUseCaptchaResult {
  trigger: (action: () => Promise<void>) => void;
  loading: boolean;
  captchaNode: React.ReactNode;
}

export const useCaptcha = (): IUseCaptchaResult => {
  const [resetCaptcha, setResetCaptcha] = useState(0);
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => Promise<void>) | null>(null);
  const [loading, setLoading] = useState(false);

  const isCaptchaEnabled = !!envConfig.SMARTCAPTCHA_CLIENT_KEY;

  const onCaptchaSuccess = async (token: string): Promise<void> => {
    if (!pendingAction) {
      return;
    }

    setCaptchaToken(token);
    setCaptchaVisible(false);

    try {
      await pendingAction();
    } finally {
      setPendingAction(null);
      setLoading(false);
      setResetCaptcha((prev) => prev + 1);
    }
  };

  const trigger = (action: () => Promise<void>): void => {
    setLoading(true);

    if (isCaptchaEnabled) {
      setPendingAction(() => action);
      setCaptchaVisible(true);
    } else {
      action().finally(() => setLoading(false));
    }
  };

  const captchaNode = isCaptchaEnabled ? (
    <InvisibleSmartCaptcha
      key={resetCaptcha}
      sitekey={envConfig.SMARTCAPTCHA_CLIENT_KEY}
      onSuccess={onCaptchaSuccess}
      visible={captchaVisible}
    />
  ) : null;

  return { trigger, loading, captchaNode };
};
